/**
 * useLogin.js
 * Handle Hodder specific PKCE like Login Flow and Set Appropriate Variables
 */

import Vue from 'vue'
import  VueCompositionApi, { ref, computed } from '@vue/composition-api'
import { TOKEN_STORAGE_KEY, VERIFIER_STORAGE_KEY, USER_ID_STORAGE_KEY, RETURN_PATH_STORAGE_KEY, STATE_STORAGE_KEY, ENTITLEMENTS_STORAGE_KEY, EXPIRY_STORAGE_KEY, IS_SHARE_LINK_KEY, SHARE_LINK_KEY} from '../utils/constants'
import { urlsafebtoa, abtobs, bstoab, parseVerify } from '../utils'

Vue.use(VueCompositionApi)

const { sign_in_url, sign_out_url, verify_base_url } = window.PASSPORT_CONFIG
const isAuthenticating = ref(true)
const availableIssues = ref([])
const loginToken = ref(null)
const userInfo = ref(null)
const grantedAccess = ref(null)


export default function useLogin() {
  const loggedIn = computed(_ => !!loginToken.value)
  const loginState = computed(_ => userInfo.value && userInfo.value.state)
  const hasAccess = computed(() => grantedAccess.value)

  async function login() {
    // Remove old token if logging in again
    await cleanUp()

    const code = crypto.getRandomValues(new Uint8Array(32))
    const code_verifier = urlsafebtoa(await abtobs(code))
    const bstoabCodeVerifier = await bstoab(code_verifier)
    // const hash = await crypto.subtle.digest('SHA-256', bstoabCodeVerifier);
    const hash = bstoabCodeVerifier;
    const code_challenge = urlsafebtoa(await abtobs(hash))
    localStorage.setItem(VERIFIER_STORAGE_KEY, code_verifier)
    localStorage.setItem(RETURN_PATH_STORAGE_KEY, window.location.href)
    let sign_in_code_url = sign_in_url.replace('code_challenge%3D', `code_challenge%3D${code_challenge}`)
    window.open(`${sign_in_code_url}`, '_self')
  }
  async function logout() {
    // Remove old token if logging in again
    await cleanUp()

    var url = window.location.href
    if (url.startsWith('https://' + window.location.hostname + '/?token=')) url = 'https://' + window.location.hostname

    localStorage.setItem(RETURN_PATH_STORAGE_KEY, url)
    window.open(`${sign_out_url}`, '_self')
  }

  async function cleanUp() {
    grantedAccess.value = false
    loginToken.value = null
    userInfo.value = null
    availableIssues.value = []
    localStorage.removeItem(TOKEN_STORAGE_KEY)
    localStorage.removeItem(USER_ID_STORAGE_KEY)
    localStorage.removeItem(STATE_STORAGE_KEY)
    localStorage.removeItem(EXPIRY_STORAGE_KEY)
    localStorage.removeItem(ENTITLEMENTS_STORAGE_KEY)
    localStorage.removeItem(IS_SHARE_LINK_KEY)
    localStorage.removeItem(SHARE_LINK_KEY)

    // Signout (delete cookie).
    const signOutUrl = new URL('sign_out', verify_base_url);
    await fetch(signOutUrl)

  }

  async function initAuth() {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY)
    if (token) return verify(token)
    const code = new URLSearchParams(window.location.search).get('code_challenge')
    if (code) await validateLogin(code)
    checkForRedirectAfterPassport()
    isAuthenticating.value = false
  }
  
  async function validateLogin(code) {
    const code_verifier = localStorage.getItem(VERIFIER_STORAGE_KEY)
    const bstoabCodeVerifier = await bstoab(code_verifier)
    // const hash = await crypto.subtle.digest('SHA-256', bstoabCodeVerifier)
    const hash = bstoabCodeVerifier;
    const code_challenge = urlsafebtoa(await abtobs(hash))

    if (code === code_challenge) {
      const token = new URLSearchParams(window.location.search).get('token')
      const uid = new URLSearchParams(window.location.search).get('uid')
      setTokens(token, uid)
    }
  }

  async function setTokens(token, uid) {
    if(token) localStorage.setItem(TOKEN_STORAGE_KEY, token)
    if(uid) localStorage.setItem(USER_ID_STORAGE_KEY, uid)
    localStorage.removeItem(VERIFIER_STORAGE_KEY)
    if(token) loginToken.value = token
  }

  async function checkForRedirectAfterPassport() {
    // Only redirect if on OIDC page
    const path = window.location.pathname;
    if (path !== '/oidc') return;
    const return_path = localStorage.getItem(RETURN_PATH_STORAGE_KEY);
    localStorage.removeItem(RETURN_PATH_STORAGE_KEY)
    if (typeof return_path !== 'undefined' && return_path !== null) {
        window.location.replace(return_path);
    } else {
        window.location.replace('//' + window.location.hostname);
    }
  }

  async function failOpen() {
    grantedAccess.value = true

    // Grant all entitlements
    var sections = document.querySelectorAll('[data-entitlement]')
    sections.forEach(section => {
      section.setAttribute('data-entitlement-granted', true);
    });
  }

  async function setAccess(issues, fromCache) {
    // Check Access
    grantedAccess.value = true
    availableIssues.value = issues
    if(!fromCache) window.location.reload()
  }

  function handleExpiry() {
    const expiry = localStorage.getItem(EXPIRY_STORAGE_KEY);
    if(!expiry) return
    const expiryDate = parseInt(expiry)
    return expiryDate > Date.now()
  }

  async function verify(token) {
    if(token) loginToken.value = token

    // Check if verify has expired
    if (handleExpiry()) {
      const state = localStorage.getItem(STATE_STORAGE_KEY);
      const issues = localStorage.getItem(ENTITLEMENTS_STORAGE_KEY);
      if (state && issues) return resolve({ state, issues, fromCache: true })
    }

    const url = new URL('verify_subscription', verify_base_url)
    const is_sharing_link = localStorage.getItem(IS_SHARE_LINK_KEY)
    if (!is_sharing_link) {
      const uid = localStorage.getItem(USER_ID_STORAGE_KEY)
      token = btoa(token + '|' + uid);
    }

    const formData = new FormData()
    formData.append('token', token)

    fetch(url, { method: 'post', body: formData })
      .then(response => response.text())
      .then(response => parseVerify(response))
      .then(response => (userInfo.value = response) && response)
      .then(response => setVerifyKeys(response))
      .then(response => resolve(response))
      .then(_ => isAuthenticating.value = false)
      .catch(err => {
        isAuthenticating.value = false
        console.log('something went wrong', err)
      })
  }

  // Cache verify for 5 hours
  function setVerifyKeys({ state, issues }) {  
    localStorage.setItem(STATE_STORAGE_KEY, state)
    localStorage.setItem(ENTITLEMENTS_STORAGE_KEY, issues)
    localStorage.setItem(EXPIRY_STORAGE_KEY, Date.now() + (5 * 60 * 60 * 1000))

    if (userInfo.value["http://schema.pugpig.com/custom_analytics/share_link"]) {
      localStorage.setItem(SHARE_LINK_KEY, userInfo.value["http://schema.pugpig.com/custom_analytics/share_link"])
    }
    return { state, issues }
  }

  function resolve({ state, issues, fromCache = false }) {
    if(state === 'unavailable') return failOpen()
    if(state === 'unknown') return logout()
    return setAccess(issues, fromCache)
  }

  return { loggedIn, loginToken, isAuthenticating, hasAccess, loginState, userInfo, availableIssues, login, logout, initAuth }
}
